// apolo/src/lib/reasoningEffort.js — the composer speed selector ↔ the model.
//
// The composer offers Esforço tiers (Low/Med/High/XHigh/Max) written to
// `ui.effort`. This maps that UI value to the API `reasoning_effort` vocabulary
// and gates it to models that actually reason — sending reasoning_effort to a
// non-reasoning model (gpt-4o, …) is a 400. The Worker forwards whatever we send
// (adapterWithCompat → reasoning_effort / anthropic output_config.effort), so the
// gate lives HERE, at the one place that knows both the UI value and the model.
// @arch:pure-engine

import { findModel } from './modelRegistry.js';

// Composer tier id → OpenAI/Anthropic effort value. Only 'med' differs from the
// API vocabulary; the rest pass through. Unknown → null (param omitted).
const TIER_TO_EFFORT = {
  low: 'low',
  med: 'medium',
  high: 'high',
  xhigh: 'xhigh',
  max: 'max',
};

/**
 * Map the composer's `ui.effort` tier to the API `reasoning_effort` value.
 * @param {string} uiEffort  'low'|'med'|'high'|'xhigh'|'max'
 * @returns {string|null}
 */
export function effortToReasoning(uiEffort) {
  if (typeof uiEffort !== 'string') return null;
  return TIER_TO_EFFORT[uiEffort.trim().toLowerCase()] || null;
}

// Providers that expose a real low/med/high EFFORT LADDER we forward (researched
// 2026-07-11 from each provider's docs): OpenAI/Groq/xAI/Mistral/DeepSeek/Kimi/
// Together take a top-level `reasoning_effort`; OpenRouter takes nested
// `reasoning.effort`; Anthropic takes `output_config.effort` (anthropicThinking).
// EXCLUDED on purpose — no ladder, so the dial would be theater: Qwen (token
// budget / enable_thinking), Z.ai GLM-5.2 and older (thinking on/off; 5.2 only
// high|max), MiniMax (thinking on/off), Google (native thinkingConfig, separate
// adapter), Ollama/local, Yi/Fugu (unconfirmed).
// GLM-5.3 is the exception: thinking cannot be disabled and it exposes a real
// low|high|max ladder — gated by id below, not by adding 'zai' to this set.
const EFFORT_PROVIDERS = new Set([
  'anthropic',
  'openai', 'groq', 'xai', 'mistral', 'deepseek', 'kimi', 'together', 'openrouter',
]);

/**
 * Does this model expose a WORKING reasoning/speed dial? True only for registry
 * models that both declare the 'reasoning' capability AND run on a provider whose
 * adapter forwards effort. Unknown/custom ids or non-forwarding providers → false
 * (so we neither show a dead dial nor send a param the model would reject).
 * @param {string} modelId
 * @returns {boolean}
 */
export function modelSupportsReasoning(modelId) {
  const entry = findModel(modelId);
  if (!entry || !Array.isArray(entry.capabilities) || !entry.capabilities.includes('reasoning')) return false;
  // GLM-5.3 always reasons and accepts reasoning_effort low|high|max (docs.z.ai).
  if (entry.id === 'zai/glm-5.3') return true;
  return EFFORT_PROVIDERS.has(entry.provider);
}

/**
 * The effort to send for a turn: the mapped tier when the model reasons, else
 * null (param omitted → the model's default, unchanged behavior).
 * @param {string} modelId
 * @param {string} uiEffort
 * @returns {string|null}
 */
export function reasoningEffortForTurn(modelId, uiEffort) {
  if (!modelSupportsReasoning(modelId)) return null;
  return effortToReasoning(uiEffort);
}
